import React from 'react'

import Modal from '../../../components/common/Modal'
import SmallButton from './SmallButton'
import { Flex } from '../../../components/FlexBox'
import { H4, Text } from '../../../components/common/Text'
import { Switch } from '../../../components/common/Switch'
import Dropdown from '../../../components/common/Dropdown'
import TextInput from '../../../ui-library/TextInput'
import useContentTopicQuery from '../../../components/common/form/hooks/useContentTopicQuery'
import useTranslation from '../../../hooks/useTranslation'
import { HERO_FEED_SLUG, HERO_SLOT_COUNT } from '../constants'
import { IFeedSettings } from '../hooks/useFeedSettings'

interface FeedSettingsModalProps {
  open: boolean
  close: () => void
  settings: IFeedSettings
  /**
   * Read-only, which is why it sits outside IFeedSettings rather than in the
   * editable form state -- the server fixes it at creation and never re-derives
   * it.
   */
  slug: string
  /**
   * Curated items currently in the draft. Only consulted for the featured
   * feed, which cannot go visible with a partly filled band.
   */
  itemCount?: number
  onChange: (patch: Partial<IFeedSettings>) => void
  /** Saves the settings on its own -- see the note on the footer below. */
  onApply: () => void
  dirty: boolean
  saving: boolean
}

/**
 * Topics use Dropdown directly rather than ContentTopicMultiselect: that
 * component hard-caps at three topics and carries event auto-fill that fires
 * even with no event. A feed has neither constraint.
 */
export const FeedSettingsModal = ({
  open,
  close,
  settings,
  slug,
  itemCount = 0,
  onChange,
  onApply,
  dirty,
  saving
}: FeedSettingsModalProps) => {
  const { t } = useTranslation()
  const { data } = useContentTopicQuery()
  const topicOptions = data?.contentTopics ?? []

  /**
   * A topic feed with no topics shows members nothing but its curated items, so
   * it must not go visible until it has one. The featured feed is exempt by
   * design -- it is boosts-only and deliberately has no topics.
   *
   * Enforced here only; the server still accepts it, so this is a guard rail
   * rather than a guarantee.
   */
  const needsTopic =
    slug !== HERO_FEED_SLUG && settings.enabled && !settings.topicIds.length

  /**
   * The mirror image of the rule above, for the one feed the topic rule exempts.
   * The featured band's cards are sized from a full band, so two items render
   * with the second larger than the first -- see the note on HERO_SLOT_COUNT.
   *
   * The band's own Publish enforces the same count; this closes the other door
   * into the same state, where a curator makes an under-filled band visible
   * without publishing anything.
   */
  const needsFullBand =
    slug === HERO_FEED_SLUG && settings.enabled && itemCount < HERO_SLOT_COUNT

  return (
    <Modal open={open} close={close} showCloseIcon>
      <Flex p={2} style={{ width: 420, maxWidth: '100%' }}>
        <H4>{t('contentCuration:settings:title')}</H4>
        <Text styles={{ fontSize: 12, marginBottom: 16 }}>
          {t('contentCuration:settings:description')}
        </Text>

        <Text styles={{ fontSize: 14, marginBottom: 4 }}>
          {t('contentCuration:list:name')}
        </Text>
        <TextInput
          value={settings.name}
          onChangeText={name => onChange({ name })}
          placeholder={t('contentCuration:create:namePlaceholder')}
          style={{ width: '100%' }}
        />

        <Text styles={{ fontSize: 14, marginBottom: 4 }}>
          {t('contentCuration:list:slug')}
        </Text>
        {/* Shown but never editable. The slug addresses this feed in
            member-facing URLs and identifies the featured feed, so it is fixed
            at creation -- renaming changes the display name only. */}
        <TextInput
          value={slug}
          editable={false}
          style={{ width: '100%', opacity: 0.6 }}
        />
        <Text styles={{ fontSize: 12, marginBottom: 16 }}>
          {t('contentCuration:settings:slugNote')}
        </Text>

        <Text styles={{ fontSize: 14, marginBottom: 4 }}>
          {t('contentCuration:create:descriptionLabel')}
        </Text>
        <TextInput
          value={settings.description}
          onChangeText={description => onChange({ description })}
          placeholder={t('contentCuration:create:descriptionPlaceholder')}
          style={{ width: '100%' }}
        />

        <Text styles={{ fontSize: 14, marginBottom: 4 }}>
          {t('contentCuration:list:topics')}
        </Text>
        <Text styles={{ fontSize: 12, marginBottom: 4 }}>
          {t('contentCuration:settings:topicsNote')}
        </Text>
        <Dropdown
          options={topicOptions}
          value={settings.topicIds}
          onSelect={topicIds => onChange({ topicIds: topicIds as string[] })}
          placeholder={t('contentCuration:settings:topicsPlaceholder')}
          isMulti
          shortMultiSelect
          closeMenuOnSelect={false}
          style={{ width: '100%' }}
        />

        <Flex flexDirection="row" alignItems="center" mb={3}>
          <Switch
            value={settings.enabled}
            onValueChange={enabled => onChange({ enabled })}
          />
          {/* Deliberately static. A label that changes with the switch makes
              the reader work out whether it describes the current state or the
              action -- the switch position already says which. */}
          <Text styles={{ fontSize: 14, marginLeft: 8 }}>
            {t('contentCuration:settings:visibilityLabel')}
          </Text>
        </Flex>

        {(needsTopic || needsFullBand) && (
          <Text
            styles={{
              fontSize: 12,
              color: '#ED435C',
              marginTop: -8,
              marginBottom: 16
            }}
          >
            {needsTopic
              ? t('contentCuration:settings:topicRequired')
              : t('contentCuration:hero:visibilityBlocked', {
                  required: HERO_SLOT_COUNT
                })}
          </Text>
        )}

        {/* Apply commits these fields on its own. The page's Publish covers the
            feed's content and ordering, which are edited outside this modal --
            two separate concerns, so two separate commits. */}
        <Flex flexDirection="row" justifyContent="flex-end">
          <SmallButton
            label={t('contentCuration:settings:apply')}
            onPress={onApply}
            disabled={
              !dirty ||
              saving ||
              !settings.name.trim() ||
              needsTopic ||
              needsFullBand
            }
            variant="primary"
          />
        </Flex>
      </Flex>
    </Modal>
  )
}

export default FeedSettingsModal
